import React, { useState } from 'react';
import {
  FiPhone,
  FiMessageSquare,
  FiShare2,
  FiGlobe,
  FiMapPin,
  FiClock,
  FiInstagram,
  FiFacebook,
  FiYoutube,
  FiLinkedin,
  FiSend,
  FiCheck,
  FiX,
  FiDownload
} from 'react-icons/fi';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import './PublicThemeRenderer.css';

const PublicThemeRenderer = ({ business, categories = [], products = [], isPreview = false }) => {
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { showToast } = useToast();

  const themeClass = `theme-${business?.theme || 'dark-premium'}`;
  const primaryColor = business?.primaryColor || '#6366F1';
  const secondaryColor = business?.secondaryColor || '#06B6D4';

  const fontClass = business?.fontStyle === 'serif' ? 'font-serif' : business?.fontStyle === 'outfit' ? 'font-outfit' : 'font-sans';
  const cardStyleClass = `card-style-${business?.cardStyle || 'glass'}`;

  const filteredProducts = selectedCategory === 'ALL'
    ? products
    : products.filter((p) => (p.categoryId?._id || p.categoryId) === selectedCategory);

  const handleCall = () => {
    if (!isPreview && business._id) {
      api.post('/analytics/phone-click', { businessId: business._id }).catch(() => {});
    }
    window.location.href = `tel:${business.phone}`;
  };

  const handleWhatsApp = (productName = '') => {
    if (!isPreview && business._id) {
      api.post('/analytics/whatsapp-click', { businessId: business._id }).catch(() => {});
    }
    let msg = `Hi ${business.businessName}, I would like to enquire about your products/services.`;
    if (productName) {
      msg = `Hi ${business.businessName}, I am interested in ordering/inquiring about "${productName}". Please share details.`;
    }
    const text = encodeURIComponent(msg);
    window.open(`https://wa.me/${business.whatsapp}?text=${text}`, '_blank');
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: business.businessName,
        text: business.description,
        url: window.location.href
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      if (showToast) showToast('Link copied to clipboard!', 'info');
    }
  };

  const handleDownloadVCard = () => {
    const vcardData = `BEGIN:VCARD
VERSION:3.0
FN:${business.businessName}
ORG:${business.businessName}
TITLE:${business.category || 'Business'}
TEL;TYPE=CELL:${business.phone}
TEL;TYPE=WORK,VOICE:${business.whatsapp || business.phone}
EMAIL:${business.email}
URL:${business.website || window.location.href}
ADR;TYPE=WORK:;;${business.address || ''};${business.city || ''};${business.state || ''};;${business.country || 'India'}
NOTE:${business.description || ''}
END:VCARD`;

    const blob = new Blob([vcardData], { type: 'text/vcard;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${business.slug || 'business'}-contact.vcf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    if (showToast) showToast('Contact vCard downloaded! Click file to save to phone contacts.', 'success');
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    if (isPreview) {
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setShowEnquiryModal(false);
      }, 2000);
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/enquiries', {
        businessId: business._id,
        ...enquiryForm
      });
      if (res.success) {
        setSubmitted(true);
        setEnquiryForm({ name: '', phone: '', email: '', message: '' });
        if (showToast) showToast('Enquiry sent successfully!', 'success');
        setTimeout(() => {
          setSubmitted(false);
          setShowEnquiryModal(false);
        }, 2000);
      }
    } catch (err) {
      if (showToast) showToast(err.message || 'Failed to submit enquiry', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const mapsSearchUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${business.address || ''} ${business.city || ''} ${business.state || ''}`
  )}`;

  return (
    <div
      className={`public-page-wrapper ${themeClass} ${fontClass} ${cardStyleClass}`}
      style={{ '--public-primary': primaryColor, '--public-secondary': secondaryColor }}
    >
      <div className="public-container">
        <div className="public-layout">
          {/* SIDEBAR — profile, contact actions, hours, address, social */}
          <aside className="public-sidebar">
            <div className="public-sidebar-card">
              <div
                className={`public-sidebar-cover ${business.coverImage ? 'has-image' : 'no-image'}`}
                style={business.coverImage ? { '--public-cover-image': `url(${business.coverImage})` } : undefined}
              />

              <div className="public-sidebar-body">
                {business.logo ? (
                  <img
                    src={business.logo}
                    alt={business.businessName}
                    className="public-avatar-img"
                  />
                ) : (
                  <div className="public-avatar-fallback">
                    {business.businessName ? business.businessName.charAt(0).toUpperCase() : 'B'}
                  </div>
                )}

                <h1 className="public-business-name">
                  {business.businessName}
                </h1>

                {business.category && (
                  <span className="public-category-badge">
                    {business.category}
                  </span>
                )}

                {business.description && (
                  <p className="public-business-desc">
                    {business.description}
                  </p>
                )}

                {/* Quick Action Bar */}
                <div className="public-quick-actions public-quick-actions--stacked">
                  {business.phone && (
                    <button onClick={handleCall} className="action-btn-call">
                      <FiPhone /> Call
                    </button>
                  )}
                  {business.whatsapp && (
                    <button onClick={() => handleWhatsApp()} className="action-btn-whatsapp">
                      <FiMessageSquare /> WhatsApp
                    </button>
                  )}
                  <button onClick={handleShare} className="action-btn-share">
                    <FiShare2 /> Share
                  </button>
                </div>

                {/* Download vCard Shortcut */}
                <button onClick={handleDownloadVCard} className="public-vcard-btn">
                  <FiDownload /> Save Contact to Phone (vCard)
                </button>

                {/* BUSINESS HOURS */}
                {business.workingHours && (
                  <div className="public-sidebar-section">
                    <h3 className="public-info-title">
                      <FiClock className="public-info-icon" /> Working Hours
                    </h3>
                    <p className="public-info-text">{business.workingHours}</p>
                  </div>
                )}

                {/* LOCATION & ADDRESS */}
                {(business.address || business.city) && (
                  <div className="public-sidebar-section">
                    <h3 className="public-info-title">
                      <FiMapPin className="public-info-icon" /> Business Address
                    </h3>
                    <p className="public-info-text">
                      {business.address}{business.address && business.city ? ', ' : ''}{business.city} {business.state} {business.country}
                    </p>
                    <a
                      href={mapsSearchUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="public-maps-link"
                    >
                      <FiGlobe /> View Location on Google Maps
                    </a>
                  </div>
                )}

                {/* SOCIAL LINKS */}
                {business.socialLinks && (
                  <div className="public-social-links">
                    {business.socialLinks.instagram && (
                      <a href={business.socialLinks.instagram} target="_blank" rel="noreferrer" className="public-social-icon public-social-instagram">
                        <FiInstagram />
                      </a>
                    )}
                    {business.socialLinks.facebook && (
                      <a href={business.socialLinks.facebook} target="_blank" rel="noreferrer" className="public-social-icon public-social-facebook">
                        <FiFacebook />
                      </a>
                    )}
                    {business.socialLinks.youtube && (
                      <a href={business.socialLinks.youtube} target="_blank" rel="noreferrer" className="public-social-icon public-social-youtube">
                        <FiYoutube />
                      </a>
                    )}
                    {business.socialLinks.linkedin && (
                      <a href={business.socialLinks.linkedin} target="_blank" rel="noreferrer" className="public-social-icon public-social-linkedin">
                        <FiLinkedin />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* MAIN — category filters, product catalogue, enquiry CTA */}
          <main className="public-main">
            {categories.length > 0 && (
              <div className="category-pill-bar">
                <button
                  onClick={() => setSelectedCategory('ALL')}
                  className={`category-pill ${selectedCategory === 'ALL' ? 'active' : ''}`}
                >
                  All Products ({products.length})
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat._id}
                    onClick={() => setSelectedCategory(cat._id)}
                    className={`category-pill ${selectedCategory === cat._id ? 'active' : ''}`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}

            <section className="public-products-section">
              <h2 className="public-section-title">
                <span>Products &amp; Services</span>
                <span className="public-section-count">
                  {filteredProducts.length} items available
                </span>
              </h2>

              {filteredProducts.length === 0 ? (
                <div className="public-empty-state">
                  <p>No products listed in this category yet.</p>
                </div>
              ) : (
                <div className="public-products-grid">
                  {filteredProducts.map((p) => (
                    <div key={p._id} className="public-product-card">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt={p.name}
                          className="public-product-img"
                          onClick={() => setSelectedProduct(p)}
                        />
                      ) : (
                        <div
                          onClick={() => setSelectedProduct(p)}
                          className="public-product-noimg"
                        >
                          No Image Available
                        </div>
                      )}

                      <div className="public-product-body">
                        <div className="public-product-header">
                          <h3
                            onClick={() => setSelectedProduct(p)}
                            className="public-product-name"
                          >
                            {p.name}
                          </h3>
                          {p.featured && (
                            <span className="public-featured-badge">
                              FEATURED
                            </span>
                          )}
                        </div>

                        {p.description && (
                          <p className="public-product-desc">
                            {p.description}
                          </p>
                        )}

                        <div className="public-product-footer">
                          <div className="public-product-price-group">
                            <span className="public-product-price">
                              ₹{p.discountPrice || p.price}
                            </span>
                            {p.discountPrice && (
                              <span className="public-product-price-original">
                                ₹{p.price}
                              </span>
                            )}
                          </div>

                          <button
                            onClick={() => handleWhatsApp(p.name)}
                            className="btn btn-sm public-enquire-btn"
                          >
                            Enquire
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ENQUIRY CTA BUTTON */}
            <div className="public-cta-wrapper">
              <button
                onClick={() => setShowEnquiryModal(true)}
                className="btn btn-primary btn-lg public-cta-btn"
              >
                <FiSend /> Send Quick Message / Enquiry
              </button>
            </div>
          </main>
        </div>

        <footer className="public-footer">
          Powered by <strong>Digital Business Kit</strong>
        </footer>
      </div>

      {/* PRODUCT DETAIL MODAL */}
      {selectedProduct && (
        <div className="modal-overlay">
          <div className="modal-content public-modal-content public-modal-content-sm">
            <div className="modal-header">
              <h3 className="modal-title">{selectedProduct.name}</h3>
              <button onClick={() => setSelectedProduct(null)} className="public-modal-close">
                <FiX />
              </button>
            </div>

            {selectedProduct.image && (
              <img src={selectedProduct.image} alt={selectedProduct.name} className="public-modal-img" />
            )}

            <div className="public-modal-price-group">
              <span className="public-modal-price">
                ₹{selectedProduct.discountPrice || selectedProduct.price}
              </span>
              {selectedProduct.discountPrice && (
                <span className="public-modal-price-original">
                  ₹{selectedProduct.price}
                </span>
              )}
            </div>

            {selectedProduct.description && (
              <p className="public-modal-desc">
                {selectedProduct.description}
              </p>
            )}

            <button
              onClick={() => {
                handleWhatsApp(selectedProduct.name);
                setSelectedProduct(null);
              }}
              className="btn btn-primary public-modal-order-btn"
            >
              Order / Enquire on WhatsApp
            </button>
          </div>
        </div>
      )}

      {/* ENQUIRY MODAL */}
      {showEnquiryModal && (
        <div className="modal-overlay">
          <div className="modal-content public-modal-content">
            <div className="modal-header">
              <h3 className="modal-title">Enquire with {business.businessName}</h3>
              <button onClick={() => setShowEnquiryModal(false)} className="public-modal-close">
                <FiX />
              </button>
            </div>

            {submitted ? (
              <div className="public-success-state">
                <FiCheck className="public-success-icon" />
                <h4 className="public-success-title">Enquiry Sent Successfully!</h4>
                <p className="public-success-text">
                  The business team will respond to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleEnquirySubmit}>
                <div className="form-group">
                  <label className="form-label">Your Name *</label>
                  <input
                    type="text"
                    required
                    className="form-control"
                    value={enquiryForm.name}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, name: e.target.value })}
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Mobile / WhatsApp Number *</label>
                  <input
                    type="tel"
                    required
                    className="form-control"
                    value={enquiryForm.phone}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, phone: e.target.value })}
                    placeholder="10-digit mobile number"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email (Optional)</label>
                  <input
                    type="email"
                    className="form-control"
                    value={enquiryForm.email}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, email: e.target.value })}
                    placeholder="yourname@example.com"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Message Details *</label>
                  <textarea
                    required
                    rows={4}
                    className="form-control"
                    value={enquiryForm.message}
                    onChange={(e) => setEnquiryForm({ ...enquiryForm, message: e.target.value })}
                    placeholder="Ask about pricing, availability, or custom orders..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary public-submit-btn"
                >
                  {submitting ? 'Sending...' : 'Submit Enquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicThemeRenderer;